// src/siteMaps.js
import paths, { rootPaths } from 'routes/paths';
import { useUserContext } from 'providers/UserProvider';
import auth from 'api/core/auth';

const getDynamicPath = (path, tenant, warehouse) => {
  if (!tenant || !warehouse || !path) return null;

  // path 가 함수일 수도 있음 → 호출해서 string 확보
  const resolvedPath =
    typeof path === 'function' ? path(tenant, warehouse) : path;

  if (typeof resolvedPath !== 'string') return null;

  return resolvedPath
    .replace(':tenant', tenant)
    .replace(':warehouse', warehouse);
};

export const useSiteMaps = () => {
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const contextReady = !!(tenant && warehouse);

  // Products Routes
  const productsRoutes = {
    label: 'Products',
    name: 'Products',
    to: getDynamicPath('products', tenant, warehouse),
    children: [
      {
        name: 'Listing',
        to: getDynamicPath('products', tenant, warehouse),
        exact: true,
      },
      {
        name: 'Manage Products',
        to: getDynamicPath('products/manage-product', tenant, warehouse),
        exact: true,
      },
    ],
  };

  // Orders Routes
  const ordersRoutes = {
    label: 'Orders',
    name: 'Orders',
    to: getDynamicPath('orders', tenant, warehouse),
    children: [
      {
        name: 'Order List',
        to: getDynamicPath('orders', tenant, warehouse),
      },
      {
        name: 'Shipments',
        to: getDynamicPath('shipments', tenant, warehouse),
      },
      {
        name: 'Manifests',
        to: getDynamicPath('manifests', tenant, warehouse),
      },
    ],
  };

  // Picklists Routes
  const picklistsRoutes = {
    label: 'Picklists',
    name: 'Picklists',
    to: getDynamicPath('picklists', tenant, warehouse),
    children: [
      {
        name: 'Picklist',
        to: getDynamicPath('picklists', tenant, warehouse),
      },
    ],
  };

  // Inventory Routes
  const inventoryRoutes = {
    label: 'Inventory',
    name: 'Inventory',
    to: getDynamicPath('inventory', tenant, warehouse),
    children: [
      {
        name: 'Inventory',
        to: getDynamicPath('inventory', tenant, warehouse),
        exact: true,
      },
      {
        name: 'Inventory History',
        to: getDynamicPath('inventory/history', tenant, warehouse),
        exact: true,
      },
    ],
  };

  // Stocktaking Routes
  const stocktakingRoutes = {
    label: 'Stocktaking',
    name: 'Stocktaking',
    to: getDynamicPath('stocktakings', tenant, warehouse),
    children: [
      {
        name: 'Stocktaking',
        to: getDynamicPath('stocktakings', tenant, warehouse),
        exact: true,
      },
    ],
  };

  // Return Routes
  const returnRoutes = {
    label: 'Return',
    name: 'Return',
    to: getDynamicPath('return-manifests', tenant, warehouse),
    children: [
      {
        name: 'Return Manifest',
        to: getDynamicPath('return-manifests', tenant, warehouse),
      },
    ],
  };

  // File Upload Routes (Bulk Action)
  const fileUploadRoutes = {
    label: 'File Upload',
    name: 'File Upload',
    to: getDynamicPath('bulk-action', tenant, warehouse),
    children: [
      {
        name: 'File Upload',
        to: getDynamicPath('bulk-action', tenant, warehouse),
      },
    ],
  };

  // Admin Routes
  const administrationRoutes = {
    label: 'Administration',
    name: 'Administration',
    to: getDynamicPath(`${rootPaths.adminRoot}`, tenant, warehouse),
    adminOnly: !auth.isManager() && !auth.isAdmin() && !auth.isSuperuser(),
    children: [
      {
        name: 'User',
        to: getDynamicPath(`${rootPaths.adminRoot}/user`, tenant, warehouse),
      },
      {
        name: 'Shelf',
        to: getDynamicPath(`${rootPaths.adminRoot}/shelf`, tenant, warehouse),
      },
      {
        name: 'Warehouse',
        to: getDynamicPath(
          `${rootPaths.adminRoot}/warehouse`,
          tenant,
          warehouse,
        ),
      },
      {
        name: 'Sequence',
        to: getDynamicPath(
          `${rootPaths.adminRoot}/sequence`,
          tenant,
          warehouse,
        ),
      },
      {
        name: 'Shipping Provider',
        to: getDynamicPath(
          `${rootPaths.adminRoot}/shipping-provider`,
          tenant,
          warehouse,
        ),
      },
    ],
  };

  // Purchase Order Routes
  const purchaseOrderRoutes = {
    label: 'Purchase Order',
    name: 'Purchase Order',
    to: getDynamicPath('purchase-orders', tenant, warehouse),
    children: [
      {
        name: 'PO List',
        to: getDynamicPath('purchase-orders', tenant, warehouse),
        exact: true,
      },
      {
        name: 'Vendor List',
        to: getDynamicPath('vendors', tenant, warehouse),
      },
    ],
  };

  // Putaway Routes
  const putawayRoutes = {
    label: 'Putaway',
    name: 'Putaway',
    to: getDynamicPath('purchase-orders/grns', tenant, warehouse),
    children: [
      {
        name: 'GRN',
        to: getDynamicPath('purchase-orders/grns', tenant, warehouse),
      },
      {
        name: 'Putaway',
        to: getDynamicPath('purchase-orders/put-away', tenant, warehouse),
      },
    ],
  };

  // Gatepass Routes
  const gatepassRoutes = {
    label: 'Gatepass',
    name: 'Gatepass',
    to: getDynamicPath(paths.gatePasses.list, tenant, warehouse),
    adminOnly: !auth.isManager() && !auth.isAdmin() && !auth.isSuperuser(),
    children: [
      {
        name: 'Gatepass List',
        to: getDynamicPath(paths.gatePasses.list, tenant, warehouse),
        exact: true,
      },
      {
        name: 'Received List',
        to: getDynamicPath(paths.gatePasses.receivedList, tenant, warehouse),
        exact: true,
      },
    ],
  };

  const maps = [
    productsRoutes,
    ordersRoutes,
    picklistsRoutes,
    purchaseOrderRoutes,
    putawayRoutes,
    gatepassRoutes,
    inventoryRoutes,
    stocktakingRoutes,
    returnRoutes,
    fileUploadRoutes,
    administrationRoutes,
  ];

  // context 준비 안 됐으면 빈 배열 반환 → Navbar에서 안전 처리
  return contextReady
    ? {
        siteMaps: maps,
        productsRoutes,
        ordersRoutes,
        picklistsRoutes,
        purchaseOrderRoutes,
        putawayRoutes,
        gatepassRoutes,
        inventoryRoutes,
        stocktakingRoutes,
        returnRoutes,
        fileUploadRoutes,
        administrationRoutes,
      }
    : { siteMaps: [] };
};
